import React, { useRef, useState } from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import styled from 'styled-components';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import IconsLib from '@/utils/IconsLib';
import Btn from '../UI/Btn';
import { useUIStore } from '@/store/useUIStore';
import {
   listPostMediaApi,
   deletePostMediaApi,
   type PostMediaItem,
} from '@/api/admin';
import { uploadPostMedia } from '@/api/postMedia';

/**
 * Pick a picture already uploaded, or add a new one — the library behind the
 * Posts editor.
 *
 * Without it, every guide that shows the same screenshot uploads it again,
 * which wastes storage and means a corrected screenshot has to be replaced in
 * five places.
 *
 * Rendered through the normal prompt system (`handleShow`), so it looks and
 * behaves like every other dialog in the dashboard.
 */

export interface PostMediaPickerData {
   /** Called with the chosen picture's address. */
   onPick: (url: string) => void;
}

interface Props {
   data: PostMediaPickerData;
}

const PostMediaPicker = ({ data }: Props) => {
   const { handleClose, handleShow } = useUIStore();
   const qc = useQueryClient();
   const fileRef = useRef<HTMLInputElement>(null);
   const [q, setQ] = useState('');
   const [busy, setBusy] = useState(false);

   const { data: res, isLoading } = useQuery({
      queryKey: ['admin', 'post-media', q],
      queryFn: () => listPostMediaApi(q.trim() || undefined),
   });

   const remove = useMutation({
      mutationFn: (id: string) => deletePostMediaApi(id),
      onSuccess: () => {
         toast.success('Removed from the library');
         qc.invalidateQueries({ queryKey: ['admin', 'post-media'] });
      },
      onError: () => toast.error('Could not remove that picture'),
   });

   const handleFile = async (file?: File | null) => {
      if (!file) return;
      setBusy(true);
      try {
         const url = await uploadPostMedia(file);
         qc.invalidateQueries({ queryKey: ['admin', 'post-media'] });
         data.onPick(url);
         handleClose();
      } catch (err) {
         toast.error((err as Error).message);
      } finally {
         setBusy(false);
         if (fileRef.current) fileRef.current.value = '';
      }
   };

   const items = res?.media ?? [];

   return (
      <Body>
         <Bar>
            <SearchInput
               value={q}
               onChange={(e) => setQ(e.target.value)}
               placeholder="Search by name"
            />
            <Btn
               title={busy ? 'Uploading…' : 'Upload new'}
               type={busy ? 'disabled' : 'normal'}
               size="small"
               bgcolor={colors.PRIMARY}
               textcolor={colors.WHITE}
               hasIcon
               icon="upload-cloud-line"
               iconPosition="left"
               onClick={() => !busy && fileRef.current?.click()}
            />
            <HiddenInput
               ref={fileRef}
               type="file"
               accept="image/webp,image/jpeg,image/png,image/gif"
               onChange={(e) => handleFile(e.target.files?.[0])}
            />
         </Bar>

         {isLoading && <SkeletonLines lines={3} />}

         {!isLoading && !items.length && (
            <Note>
               <b>No pictures yet.</b>
               Upload one and it stays here for every other post.
            </Note>
         )}

         {!!items.length && (
            <Grid>
               {items.map((m: PostMediaItem) => (
                  <Tile key={m._id}>
                     <Thumb
                        type="button"
                        onClick={() => {
                           data.onPick(m.url);
                           handleClose();
                        }}
                     >
                        <img src={m.url} alt={m.name} loading="lazy" />
                     </Thumb>
                     <TileFoot>
                        <TileName title={m.name}>{m.name || 'Untitled'}</TileName>
                        <Trash
                           type="button"
                           title="Remove from the library"
                           onClick={() =>
                              handleShow({
                                 template: 'confirm-action',
                                 title: 'Remove from the library?',
                                 windowType: 'desktop',
                                 data: {
                                    message: `“${m.name || 'This picture'}” will no longer be offered here.`,
                                    detail:
                                       'Guides already using it keep working — the file itself stays where it is.',
                                    confirmLabel: 'Remove',
                                    danger: true,
                                    onConfirm: () => remove.mutate(m._id),
                                 },
                              })
                           }
                        >
                           <IconsLib icon="delete-bin-6-line" size="16px" color={colors.GRAY5} />
                        </Trash>
                     </TileFoot>
                  </Tile>
               ))}
            </Grid>
         )}
      </Body>
   );
};

export default PostMediaPicker;

/* ─── Styles ────────────────────────────────────────────────────────── */

const Body = styled.div`
   width: min(680px, 78vw);
   max-height: 62vh;
   overflow-y: auto;
   padding-top: 4px;

   @media (max-width: 768px) {
      width: 100%;
      max-height: none;
   }
`;

const Bar = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   margin-bottom: 14px;
`;

const SearchInput = styled.input`
   flex: 1;
   font-size: 13px;
   padding: 9px 11px;
   border: 1px solid ${colors.GRAY11};
   border-radius: 8px;
   background: ${colors.WHITE};
   color: ${colors.GRAY_DARK};

   &:focus {
      outline: none;
      border-color: ${colors.PRIMARY};
   }
`;

const HiddenInput = styled.input`
   display: none;
`;

const Grid = styled.div`
   display: grid;
   grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
   gap: 12px;
`;

const Tile = styled.div`
   border: 1px solid ${colors.GRAY11};
   border-radius: 8px;
   overflow: hidden;
   background: ${colors.WHITE};
`;

const Thumb = styled.button`
   display: block;
   width: 100%;
   aspect-ratio: 4 / 3;
   border: 0;
   padding: 0;
   background: ${colors.GRAY14};
   cursor: pointer;

   img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
   }

   &:hover img {
      opacity: 0.88;
   }
`;

const TileFoot = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
   padding: 7px 9px;
   border-top: 1px solid ${colors.GRAY13};
`;

const TileName = styled.span`
   font-size: 12px;
   color: ${colors.GRAY_DARK};
   white-space: nowrap;
   overflow: hidden;
   text-overflow: ellipsis;
   flex: 1;
`;

const Trash = styled.button`
   border: 0;
   background: none;
   cursor: pointer;
   padding: 2px;
   display: grid;
   place-items: center;
`;

const Note = styled.div`
   padding: 34px 10px;
   text-align: center;
   color: ${colors.GRAY3};
   font-size: 13.5px;

   b {
      display: block;
      color: ${colors.GRAY_DARK};
      font-size: 15px;
      margin-bottom: 4px;
   }
`;
